export interface GuideStop {
  time: string;
  title: string;
  details: string;
  duration?: string;
  travelTip?: string;
}

export interface GuideSection {
  title: string;
  intro?: string;
  items: string[];
}

export interface GuideContent {
  slug: string;
  title: string;
  summary: string;
  badge: string;
  coverImage: string;
  category: "itinerary" | "activities";
  readingMinutes: number;
  bestFor: string;
  startFrom: string;
  vibe: string;
  highlights: string[];
  itinerary?: GuideStop[];
  sections?: GuideSection[];
}

export const GuidesContent: GuideContent[] = [
  {
    slug: "1-day-galle-guide",
    title: "1-Day Guide To Galle",
    summary:
      "A compact yet memorable day route from Nutmeg Grove with fort heritage, local food stops, and a scenic sunset finish.",
    badge: "1 Day",
    coverImage: "/ng-images/1dayguide-ng.png",
    category: "itinerary",
    readingMinutes: 4,
    bestFor: "Short stays and first-time visitors",
    startFrom: "Nutmeg Grove",
    vibe: "Efficient and highlight-focused",
    highlights: ["Walkable plan", "Local food", "Sunset spot"],
    itinerary: [
      {
        time: "7:30 AM",
        title: "Breakfast At Nutmeg Grove",
        details:
          "Start with a light breakfast and pack water, hat, and sunscreen for the day.",
        duration: "45 min",
      },
      {
        time: "9:00 AM",
        title: "Explore Galle Fort Streets",
        details:
          "Walk through Church Street and Pedlar Street, visit old Dutch buildings, and stop at small galleries.",
        duration: "2 hrs",
      },
      {
        time: "11:30 AM",
        title: "Maritime Museum + Lighthouse",
        details:
          "Visit the museum for local history, then walk to the lighthouse for ocean views.",
        duration: "1 hr 30 min",
      },
      {
        time: "1:00 PM",
        title: "Lunch In The Fort",
        details:
          "Try seafood rice, kottu, or local curry bowls at a cafe inside the fort.",
        duration: "1 hr",
      },
      {
        time: "2:30 PM",
        title: "Dutch Hospital Precinct",
        details:
          "Browse boutique shops and relax with a fresh juice or coffee.",
        duration: "1 hr",
      },
      {
        time: "4:00 PM",
        title: "Unawatuna Beach Break",
        details: "Head to Unawatuna for a swim or easy beach walk.",
        duration: "2 hrs",
        travelTip: "Use a tuk-tuk from fort area for quick transfer.",
      },
      {
        time: "6:15 PM",
        title: "Sunset At Flag Rock",
        details:
          "Return to Galle Fort and watch sunset from Flag Rock Bastion.",
        duration: "45 min",
      },
      {
        time: "7:30 PM",
        title: "Dinner + Return",
        details:
          "Have dinner in town and return to Nutmeg Grove for a relaxed evening.",
        duration: "1 hr 30 min",
      },
    ],
  },
  {
    slug: "2-day-galle-guide",
    title: "2-Day Guide To Galle",
    summary:
      "A balanced two-day flow that combines fort culture, coast time, viewpoint stops, and relaxed cafe breaks.",
    badge: "2 Days",
    coverImage: "/ng-images/2dayguide-ng.png",
    category: "itinerary",
    readingMinutes: 5,
    bestFor: "Couples and relaxed city-break travelers",
    startFrom: "Nutmeg Grove",
    vibe: "Balanced pace with room to explore",
    highlights: ["Fort + coast", "Balanced pace", "Easy transfers"],
    itinerary: [
      {
        time: "Day 1 - 8:00 AM",
        title: "Fort Heritage Walk",
        details:
          "Start with key heritage landmarks: Ramparts, lighthouse, museum, and old churches.",
      },
      {
        time: "Day 1 - 1:00 PM",
        title: "Lunch + Boutique Time",
        details:
          "Explore boutiques and local artisan stores around Pedlar Street and Hospital Street.",
      },
      {
        time: "Day 1 - 4:30 PM",
        title: "Sunset At Fort Walls",
        details:
          "Finish day one with sunset and street snacks around the fort.",
      },
      {
        time: "Day 2 - 9:00 AM",
        title: "Jungle Beach Or Unawatuna",
        details: "Pick one beach for swimming and a slow morning by the sea.",
      },
      {
        time: "Day 2 - 12:30 PM",
        title: "Lunch With Sea View",
        details:
          "Choose a beachside cafe and try grilled seafood or rice and curry.",
      },
      {
        time: "Day 2 - 3:00 PM",
        title: "Rumassala + Japanese Peace Pagoda",
        details:
          "Do a short viewpoint stop and nature walk with panoramic views.",
      },
      {
        time: "Day 2 - 6:30 PM",
        title: "Back To Galle For Dinner",
        details:
          "Wrap up with dinner in town and optional night walk inside the fort.",
      },
    ],
  },
  {
    slug: "3-day-galle-guide",
    title: "3-Day Guide To Galle",
    summary:
      "A richer three-day experience with heritage walks, beach time, food trails, and optional nearby excursions.",
    badge: "3 Days",
    coverImage: "/ng-images/3dayguide-ng.png",
    category: "itinerary",
    readingMinutes: 6,
    bestFor: "Guests who want depth, not just highlights",
    startFrom: "Nutmeg Grove",
    vibe: "Slow travel and local immersion",
    highlights: [
      "Deeper local experience",
      "Flexible add-ons",
      "Culture + nature",
    ],
    itinerary: [
      {
        time: "Day 1",
        title: "Galle Fort Core Experience",
        details:
          "Landmarks, museum, lighthouse, shopping lanes, and sunset at the ramparts.",
      },
      {
        time: "Day 2",
        title: "Beaches + Viewpoints",
        details:
          "Unawatuna, Jungle Beach, Rumassala, and Japanese Peace Pagoda.",
      },
      {
        time: "Day 3 - Morning",
        title: "Food + Market Trail",
        details:
          "Local breakfast, fruit market, spice stores, and street-food stops.",
      },
      {
        time: "Day 3 - Afternoon",
        title: "Optional Excursion",
        details:
          "Choose one: Hikkaduwa, Koggala Lake boat tour, or a tea and village tour.",
      },
      {
        time: "Day 3 - Evening",
        title: "Slow Closing Evening",
        details:
          "Return for a final sunset and a calm dinner close to Nutmeg Grove.",
      },
    ],
  },
  {
    slug: "top-10-activities-galle",
    title: "Top 10 Activities In Galle",
    summary:
      "Ten high-value experiences that consistently delight Nutmeg Grove guests, from fort walls to beach sunsets.",
    badge: "Top 10",
    coverImage: "/ng-images/top-placetovisit-ng.png",
    category: "activities",
    readingMinutes: 4,
    bestFor: "Anyone deciding what to do first",
    startFrom: "Nutmeg Grove",
    vibe: "Curated essentials",
    highlights: ["Most popular", "High value", "Beginner friendly"],
    sections: [
      {
        title: "Must-Do List",
        intro:
          "If your time is limited, start with these ten proven experiences for a well-rounded Galle trip.",
        items: [
          "Walk the Galle Fort ramparts at sunrise.",
          "Visit the Galle Lighthouse and nearby bastions.",
          "Try a local rice and curry lunch in the fort.",
          "Catch sunset from Flag Rock Bastion.",
          "Explore boutique stores at Dutch Hospital.",
          "Swim at Unawatuna Beach.",
          "Take a tuk-tuk to Jungle Beach.",
          "Visit Japanese Peace Pagoda viewpoint.",
          "Try Sri Lankan street snacks in the evening.",
          "Take a night walk in Galle Fort lanes.",
        ],
      },
    ],
  },
  {
    slug: "100-things-to-do-galle",
    title: "100 Things To Do In Galle",
    summary:
      "A deep idea bank covering heritage, food, beaches, culture, and slow-travel moments around Galle.",
    badge: "100 Ideas",
    coverImage: "/ng-images/100thingsaroundgalle-ng.png",
    category: "activities",
    readingMinutes: 10,
    bestFor: "Long stays, repeat visitors, and explorers",
    startFrom: "Nutmeg Grove",
    vibe: "Mix-and-match exploration",
    highlights: ["Comprehensive", "Mix of budgets", "Great for longer stays"],
    sections: [
      {
        title: "Heritage And History (1-20)",
        intro:
          "Start with the roots of Galle through fort architecture, museums, and historical landmarks.",
        items: [
          "Walk the full fort wall loop.",
          "Visit the Maritime Museum.",
          "Photograph the Clock Tower.",
          "Visit the lighthouse at golden hour.",
          "See Dutch Reformed Church interiors.",
          "Observe old colonial gates.",
          "Take a heritage architecture walk.",
          "Sketch an old fort facade.",
          "Visit the National Museum in fort area.",
          "Read historical markers along the walls.",
          "Watch local cricket near historic grounds.",
          "Join a guided fort history walk.",
          "Visit the old courthouse buildings.",
          "Explore hidden alleys and lanes.",
          "Capture black-and-white street photos.",
          "Attend a local history talk if available.",
          "Visit nearby temple and church sites.",
          "Collect postcards of old Galle.",
          "Find the oldest surviving street signs.",
          "Map a self-guided fort route.",
        ],
      },
      {
        title: "Food And Cafe Trail (21-40)",
        intro:
          "Taste your way through local staples, short eats, seafood, and cozy cafe experiences.",
        items: [
          "Try classic rice and curry.",
          "Taste kottu roti from a local shop.",
          "Eat hoppers for breakfast.",
          "Sample string hoppers and curry.",
          "Try fresh king coconut water.",
          "Eat seafood grilled by the coast.",
          "Explore bakery snacks in town.",
          "Try Sri Lankan milk tea.",
          "Taste local sambols with meals.",
          "Find a dessert cafe in fort lanes.",
          "Do a cafe hop afternoon.",
          "Try fruit juice blends.",
          "Have dinner at Dutch Hospital.",
          "Try spicy street cutlets.",
          "Sample local short eats platter.",
          "Try crab curry at a specialty spot.",
          "Taste jaggery sweets.",
          "Get takeaway for a beach picnic.",
          "Try vegetarian curry plates.",
          "Join a short cooking class.",
        ],
      },
      {
        title: "Beaches And Nature (41-60)",
        intro:
          "Blend ocean time with viewpoint stops and nature experiences around the southern coast.",
        items: [
          "Morning walk at Unawatuna.",
          "Swim at Jungle Beach.",
          "Snorkel in calm waters.",
          "Watch sunset from the beach.",
          "Take a beach yoga session.",
          "Visit Rumassala trails.",
          "See ocean views from Peace Pagoda.",
          "Try paddleboarding if available.",
          "Photograph palm-lined coastlines.",
          "Collect seashell textures for photos.",
          "Visit Koggala Lake area.",
          "Take a mangrove boat ride.",
          "Birdwatch near wetlands.",
          "Plan a sunrise photography hour.",
          "Enjoy a no-phone beach hour.",
          "Take a picnic breakfast outdoors.",
          "Visit a turtle conservation center.",
          "Spend an evening by beach cafes.",
          "Watch local fishermen routines.",
          "Take a short coastal tuk-tuk ride.",
        ],
      },
      {
        title: "Shopping And Culture (61-80)",
        intro:
          "Discover handmade goods, local craftsmanship, and street-level cultural moments.",
        items: [
          "Browse local craft shops.",
          "Buy handloom items.",
          "Find batik clothing pieces.",
          "Pick up handmade postcards.",
          "Shop tea and spice packs.",
          "Visit local art galleries.",
          "Buy cinnamon and curry spices.",
          "Find handmade jewelry.",
          "Explore weekend market stalls.",
          "Collect local ceramic pieces.",
          "Find eco-friendly souvenirs.",
          "Attend a small art event.",
          "Chat with local shop owners.",
          "Try a calligraphy name card.",
          "Get custom tote prints.",
          "Photograph colorful storefronts.",
          "Explore bookshops and stationery.",
          "Find local music and instruments.",
          "Visit a local tailoring shop.",
          "Bring back a Galle map print.",
        ],
      },
      {
        title: "Short Trips And Slow Moments (81-100)",
        intro:
          "Use these ideas for low-pressure days, nearby town hops, and reflective travel moments.",
        items: [
          "Take a day trip to Hikkaduwa.",
          "Visit nearby stilt-fishing viewpoints.",
          "Do a half-day village tour.",
          "Take a slow morning at Nutmeg Grove garden.",
          "Read a book in a shaded cafe.",
          "Take a sketchbook walk.",
          "Capture a travel journal entry.",
          "Watch rain from a verandah cafe.",
          "Try an early local bus ride experience.",
          "Explore side streets by bicycle.",
          "Visit a local temple respectfully.",
          "Try a meditation session.",
          "Do a digital detox afternoon.",
          "Watch a local sports game.",
          "Take an evening street portrait set.",
          "Plan a themed food day.",
          "Take one landmark photo at each hour.",
          "Meet a local guide for hidden spots.",
          "Create your own top-5 list from the trip.",
          "End with a final sunset and thank-you note.",
        ],
      },
    ],
  },
];

export const getGuideBySlug = (slug?: string) =>
  GuidesContent.find((guide) => guide.slug === slug);
